import styled from "styled-components";
import {
  border,
  brand,
  radius,
  spacing,
  status,
  surface,
  text,
  typography,
} from "../../theme/tokens";

/**
 * Plan-of-record panel chrome. Rows reuse the AI panel's inbox-list
 * vocabulary (InboxItemRow etc.) and the Work panel's lane headers and
 * chips; what lives here is the plan-specific dressing: the status
 * banner (approved / draft / stale), the "now → target" line, the
 * branch table (if granted / if denied / if partial) and the version
 * history.
 */

/** Approved = quiet green; draft = amber "needs you"; stale = red. */
export const PlanStatusBanner = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[3]};
  padding: ${spacing[2]} ${spacing[3]};
  margin: ${spacing[2]} 0 ${spacing[3]} 0;
  border-radius: ${radius.md};
  font-size: ${typography.size.sm};
  line-height: 1.45;
  background: ${({ $tone }) =>
    $tone === "stale"
      ? status.failed.bg
      : $tone === "draft"
        ? status.review.bg
        : status.ready.bg};
  color: ${({ $tone }) =>
    $tone === "stale"
      ? status.failed.fg
      : $tone === "draft"
        ? status.review.fg
        : status.ready.fg};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "stale"
        ? status.failed.border
        : $tone === "draft"
          ? status.review.border
          : status.ready.border};

  strong {
    font-weight: ${typography.weight.semibold};
  }
`;

export const PlanStatusText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

export const PlanStatusActions = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: ${spacing[1.5]};
`;

/** The one-paragraph summary in the attorney's voice. */
export const PlanSummary = styled.p`
  margin: 0 0 ${spacing[2]} 0;
  font-size: ${typography.size.sm};
  line-height: 1.5;
  color: ${text.primary};
`;

/** "Now 70% → target 100% (TDIU)". */
export const PlanTargetLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${spacing[1.5]};
  margin-bottom: ${spacing[2]};
  font-size: ${typography.size.sm};
  color: ${text.secondary};

  strong {
    font-size: ${typography.size.md};
    font-weight: ${typography.weight.semibold};
    color: ${text.primary};
  }
`;

/** Lane / goal / status chips on an issue. `$tone` "lane" paints the
 * lane in play in brand blue so it reads first. */
export const PlanChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: 0 ${spacing[1.5]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.medium};
  line-height: 18px;
  border-radius: ${radius.sm};
  white-space: nowrap;
  background: ${({ $tone }) =>
    $tone === "lane"
      ? brand.primaryLight
      : $tone === "won"
        ? status.ready.bg
        : $tone === "over"
          ? status.failed.bg
          : $tone === "flag"
            ? status.review.bg
            : status.neutral.bg};
  color: ${({ $tone }) =>
    $tone === "lane"
      ? brand.primary
      : $tone === "won"
        ? status.ready.fg
        : $tone === "over"
          ? status.failed.fg
          : $tone === "flag"
            ? status.review.fg
            : text.secondary};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "lane"
        ? "transparent"
        : $tone === "won"
          ? status.ready.border
          : $tone === "over"
            ? status.failed.border
            : $tone === "flag"
              ? status.review.border
              : border.light};
`;

export const PlanChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[1]};
  margin-top: ${spacing[1]};
`;

/** Condition name on an issue row, with the goal beside it. */
export const PlanIssueTitle = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${spacing[1.5]};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};

  span {
    font-weight: ${typography.weight.regular};
    color: ${text.secondary};
  }
`;

/** A labelled line inside an issue or loop: "Step", "Theory",
 * "Evidence needed". Label is muted; the value carries the weight. */
export const PlanField = styled.div`
  margin-top: ${spacing[1.5]};
  font-size: ${typography.size.sm};
  line-height: 1.45;
  color: ${text.primary};

  strong {
    font-weight: ${typography.weight.semibold};
    color: ${text.secondary};
    margin-right: ${spacing[1]};
  }

  ul {
    margin: ${spacing[0.5]} 0 0 0;
    padding-left: ${spacing[4]};
  }

  li {
    margin: 0;
  }
`;

/** The branches — what happens on each outcome of the filing we are
 * waiting on. Three rows; the one the letter triggered is painted. */
export const PlanBranches = styled.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: ${spacing[2]};
  row-gap: ${spacing[1]};
  margin: ${spacing[1.5]} 0 0 0;
  padding: ${spacing[1.5]} ${spacing[2]};
  font-size: ${typography.size.sm};
  line-height: 1.45;
  background: ${surface.hover};
  border-radius: ${radius.sm};

  dt {
    margin: 0;
    font-weight: ${typography.weight.semibold};
    color: ${text.secondary};
    white-space: nowrap;
  }

  dd {
    margin: 0;
    color: ${text.primary};
  }
`;

/** "L1" badge on an open loop — the id the model's loop match names. */
export const PlanLoopId = styled.span`
  display: inline-block;
  min-width: 24px;
  padding: 0 ${spacing[1]};
  margin-right: ${spacing[1.5]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  line-height: 18px;
  text-align: center;
  border-radius: ${radius.sm};
  background: ${brand.primary};
  color: ${surface.surface};
`;

/** Collapsible group header for the won / parked / closed issues. */
export const PlanGroupToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  margin-top: ${spacing[3]};
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${text.muted};

  &:hover {
    color: ${text.secondary};
  }
`;

/** One line per version: "v3 · approved Oct 19, 2026 · plan of record". */
export const PlanVersionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[1.5]};
  padding: ${spacing[1]} 0;
  font-size: ${typography.size.xs};
  color: ${text.secondary};
  border-top: 1px solid ${border.light};

  &:first-of-type {
    border-top: none;
  }

  strong {
    font-weight: ${typography.weight.semibold};
    color: ${text.primary};
  }
`;

export const PlanHint = styled.p`
  margin: ${spacing[2]} 0 0 0;
  font-size: ${typography.size.sm};
  line-height: 1.45;
  color: ${text.muted};
`;

export const PlanLinkButton = styled.button`
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  font-weight: ${typography.weight.semibold};
  color: inherit;
  text-decoration: underline;

  &:hover {
    color: ${brand.primaryHover};
  }
`;
